import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsNoMatch'));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Something went wrong');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <PageLayout>
        <div className="min-h-[75vh] flex items-center justify-center py-12 px-6">
          <div className="text-center" data-testid="reset-password-no-token">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-taxera-charcoal mb-3">{t('auth.invalidResetLink')}</h1>
            <p className="text-sm text-slate-500 mb-6">{t('auth.invalidResetLinkDesc')}</p>
            <Link to="/forgot-password" className="text-taxera-blue font-semibold text-sm hover:underline" data-testid="request-new-link">
              {t('auth.requestNewLink')}
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="min-h-[75vh] flex items-center justify-center py-12 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {success ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-taxera-charcoal mb-3">{t('auth.passwordReset')}</h1>
              <p className="text-sm text-slate-500 mb-6">{t('auth.passwordResetSuccess')}</p>
              <Link
                to="/login"
                className="inline-block px-6 py-3 taxera-gradient text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all"
                data-testid="go-to-login-btn"
              >
                {t('auth.signIn')}
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="font-heading text-3xl font-bold text-taxera-charcoal mb-2" data-testid="reset-password-title">
                  {t('auth.resetTitle')}
                </h1>
                <p className="text-sm text-slate-500">
                  {t('auth.resetSubtitle')}
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-600 rounded-xl text-sm mb-4" data-testid="reset-password-error">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.newPassword')}
                    required
                    minLength={8}
                    className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-taxera-blue focus:ring-1 focus:ring-taxera-blue outline-none"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="reset-toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.confirmNewPassword')}
                    required
                    minLength={8}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-taxera-blue focus:ring-1 focus:ring-taxera-blue outline-none"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 taxera-gradient text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="reset-password-submit"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('auth.resetPassword')}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default ResetPasswordPage;
